import React from 'react';
import { Container } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import ApiClient from '../ApiClient';
import LoadingView from './loadingView';
import './paymentCheckout.css';
import { PaymentMethodType, SessionStatus, TransactionStatus } from '../common/constants';
import UTILS from '../common/utils';
import PoweredByBox from '../common/poweredByBox';
import InstapayPaymentForm from '../Components/PaymentForms/instapay/InstapayPaymentForm';
import ApplePayForm from '../Components/PaymentForms/apple-pay/ApplePayForm';
import { ValidateNumeric, FormatCardNumber, FormatExpiryDate } from './Checkout/utils';
import AuthenticationView from './AuthenticationView';
import HostedPaymentCheckoutV2 from './Checkout/hostedPaymentCheckoutV2';
import { prepareOtpAuthentication } from '../common/otpSetup';

const { pay, setupOtp, getAuthenticationView, getAuthenticationStatus, getTransactionSessionInfo } = ApiClient();

class HostedPaymentCheckout extends React.Component {
    constructor(props) {
        super(props);
        let showNotFound = false;
        let params = new URLSearchParams(window.location.search);
        if (params.has('token')) {
            this.authToken = params.get('token');
        } else {
            showNotFound = true;
        }
        if (params.has('session_id')) {
            this.sessionId = params.get('session_id');
        } else {
            this.sessionId = null;
        }
        if (params.has('use_card_details_v2')) {
            this.useCardDetailsV2 = params.get('use_card_details_v2');
        } else {
            this.useCardDetailsV2 = false;
        }

        this.primaryColor = UTILS.getPrimaryColor();

        this.state = {
            form: {
                first_name: "",
                last_name: "",
                payment_number: "",
                expiry_date: "",
                cvv: "",
            },
            valid_payment_number: false,
            valid_expiration_month: false,
            valid_expiration_year: false,
            valid_first_name: false,
            valid_last_name: false,
            valid_cvv: false,
            show_payment_success: false,
            show_loading: false,
            show_not_found: showNotFound,
            authentication_view_html: '',
            authentication_view_url: '',
            show_authentication_view: false,
            pay_button_clicked: false,
            payment_method_type: PaymentMethodType.Unknown,
            is_bank_card_enabled: false,
            is_meeza_card_enabled: false,
            is_wallet_enabled: false,
            is_instapay_enabled: false,
            show_payment_completed: false,
            is_3ds_required: false,
            should_save_payment_method: false,
            success_url: '',
            failure_url: '',
            amount: null,
            is_save_card_checkbox_enabled: false,
            should_show_amount: false,
            show_payment_tabs: true,
            currency: '',
            is_apple_pay_enabled: false,
            merchant_display_name: '',
            apple_pay_merchant_id: '',
            is_payment_method_type_preselected: false,
        };
    }

    isApplePayAvailable() {
        try {
            return typeof window != "undefined" && window.ApplePaySession && window.ApplePaySession.canMakePayments();
        } catch (error) {
            return false
        }
    }

    handleChange = (field) => (e) => {
        const rawValue = e?.target?.value ?? "";
        this.setState((prev) => {
            const isWalletNumberField = field === "payment_number" && prev.payment_method_type === PaymentMethodType.Wallet;
            const nextValue = isWalletNumberField ? rawValue.replace(/\D/g, "") : rawValue;
            const nextState = {
                form: { ...prev.form, [field]: nextValue },
            };

            if (field === "payment_number" && prev.pay_button_clicked) {
                nextState.valid_payment_number = prev.payment_method_type === PaymentMethodType.Wallet
                    ? nextValue.length > 0
                    : nextValue.length === 19;
            }

            return nextState;
        });
    };

    getPaymentNumberDigits = () => {
        return (this.state.form.payment_number || "").replace(/\D/g, "");
    }

    async componentDidMount() {
        window.addEventListener('message', this.onRedirectMessage);
        if (!this.state.show_not_found) {
            await this.getSessionInfo();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('message', this.onRedirectMessage);
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    validateFormFields() {
        if (this.state.payment_method_type === PaymentMethodType.Wallet) {
            return this.validateWalletNumber();
        }

        return this.validatePaymentNumber() &
            this.validateExpirationMonth() &
            this.validateExpirationYear() &
            this.validateCVV() &
            this.validateFirstName() &
            this.validateLastName();
    }

    validateWalletNumber() {
        const valid = this.getPaymentNumberDigits().length > 0;
        this.setState({ "valid_payment_number": valid });
        return valid;
    }

    validatePaymentNumber() {
        // 19 because of the 3 separated spaces
        const valid = this.state.form.payment_number.length === 19;
        this.setState({ "valid_payment_number": valid });
        return valid;
    }

    validateFirstName() {
        const valid = this.state.form.first_name.length > 0;
        this.setState({ "valid_first_name": valid });
        return valid;
    }

    validateLastName() {
        const valid = this.state.form.last_name.length > 0;
        this.setState({ "valid_last_name": valid });
        return valid;
    }

    getExpirationMonth() {
        return +this.state.form.expiry_date.substring(0, 2);
    }

    getExpirationYear() {
        const year = Math.floor(new Date().getFullYear() / 100) * 100;
        return year + (+this.state.form.expiry_date.substring(3, 5));
    }

    validateExpirationMonth() {
        const month = this.getExpirationMonth();
        const valid = month >= 1 && month <= 12;
        this.setState({ "valid_expiration_month": valid });
        return valid;
    }

    validateExpirationYear() {
        const valid = this.state.form.expiry_date.length === 5;
        this.setState({ "valid_expiration_year": valid });
        return valid;
    }

    validateCVV() {
        const valid = this.state.form.cvv.length === 3;
        this.setState({ "valid_cvv": valid });
        return valid;
    }

    onSubmit = async (e) => {
        e.preventDefault();
        this.setState({ pay_button_clicked: true });
        if (this.validateFormFields()) {
            if (this.state.is_3ds_required || this.state.payment_method_type === PaymentMethodType.Wallet) {
                await this.getAuthView();
            } else {
                await this.pay();
            }
        }
    }

    async getAuthView() {
        const requestDto = this.createGetAuthenticationViewRequestDto();
        this.setState({ "show_loading": true });

        try {
            if (this.state.payment_method_type === PaymentMethodType.BankCard) {
                await prepareOtpAuthentication({
                    setupOtp,
                    requestDto,
                    authToken: this.authToken,
                });
            }

            await getAuthenticationView(requestDto, this.authToken, this.onGetAuthViewSuccess, this.onError);
        } catch (error) {
            this.onAuthenticationSetupError(error);
        }
    }

    onAuthenticationSetupError = (error) => {
        this.setState({ "show_loading": false });

        const errorMessage = error?.message ?? "Something went wrong";
        alert(errorMessage);

        if (this.state.failure_url) {
            window.location.replace(
                this.state.failure_url
                + "?error_code=" + (error?.errorCode ?? '')
                + "&error_message=" + encodeURIComponent(errorMessage));
        }
    }

    async checkAuthenticationStatus() {
        await getAuthenticationStatus(this.authToken, this.sessionId, this.onGetAuthStatusSuccess, (error) => console.log(error));
    }

    async getSessionInfo() {
        this.setState({ "show_loading": true });
        await getTransactionSessionInfo(this.authToken, this.sessionId, this.onGetSessionInfosSuccess, this.onError);
    }

    async pay() {
        this.setState({ "show_loading": true });
        const requestDto = this.createPayRequestDto();
        await pay(requestDto, this.authToken, this.onPaySuccess, this.onError);
    }

    createPayRequestDto = () => {
        if (this.state.payment_method_type === PaymentMethodType.Wallet) {
            return {
                "payment_number": this.getPaymentNumberDigits(),
                "session_id": this.sessionId,
            }
        }

        return {
            "payment_number": this.state.form.payment_number.replace(/ /g, ''),
            "expiration_month": this.getExpirationMonth(),
            "expiration_year": this.getExpirationYear(),
            "first_name": this.state.form.first_name,
            "last_name": this.state.form.last_name,
            "security_code": this.state.form.cvv,
            "should_save_payment_method": this.state.should_save_payment_method,
            "session_id": this.sessionId,
        }
    }

    postMessage = (message) => {
        window.parent.postMessage(message, "*")
    }

    onRedirectMessage = (event) => {
        if (!event || !event.data) {
            return;
        }

        if (event.origin && (event.origin !== window.location.origin)) {
            return;
        }

        const payload = event.data;
        if (typeof payload !== 'object') {
            return;
        }

        if (payload.status) {
            const isSuccessful = payload.status === TransactionStatus.Successful;

            if (this.intervalId) {
                clearInterval(this.intervalId);
            }

            if (!isSuccessful) {
                if (this.state.failure_url) {
                    const url = new URL(this.state.failure_url);
                    url.searchParams.set('error_code', payload.error_code);
                    url.searchParams.set('error_message', payload.error_message);
                    window.location.replace(url);
                } else {
                    alert(payload.error_message);
                }
            } else {
                this.setState({ "show_authentication_view": false });
                this.pay();
            }
        }
    }

    createGetAuthenticationViewRequestDto = () => {
        if (this.state.payment_method_type === PaymentMethodType.Wallet) {
            return {
                "payment_number": this.getPaymentNumberDigits(),
                "session_id": this.sessionId,
            }
        }

        return {
            "payment_number": this.state.form.payment_number.replace(/ /g, ''),
            "expiration_month": this.getExpirationMonth(),
            "expiration_year": this.getExpirationYear(),
            "first_name": this.state.form.first_name,
            "last_name": this.state.form.last_name,
            "session_id": this.sessionId,
            "cvv": this.state.form.cvv,
        }
    }

    ObjectContains = (dtoObject, internalObject) => {
        return dtoObject !== null && internalObject in dtoObject;
    }

    onGetAuthViewSuccess = (responseDto) => {
        this.setState({ "show_loading": false });
        if (this.ObjectContains(responseDto, "succeeded") && responseDto["succeeded"]) {
            this.setState({ "show_authentication_view": true });
            this.setState({ "authentication_view_html": responseDto["otpHtmlContent"] });
            this.setState({ "authentication_view_url": responseDto["otpUrl"] });
            this.intervalId = setInterval(this.checkAuthenticationStatus.bind(this), 5000);
        }
        else if (this.ObjectContains(responseDto, "succeeded") && !responseDto["succeeded"]) {
            alert(responseDto["error_message"]);
            window.location.replace(this.state.failure_url + "?error_code=" + responseDto["error_code"] + "&error_message=" + responseDto["error_message"]);
        }
        else {
            alert("Something went wrong");
        }
    }

    onGetAuthStatusSuccess = (responseDto) => {
        if (this.ObjectContains(responseDto, "succeeded") && responseDto["succeeded"]) {
            if (responseDto["status"] === "InProgress") {
                return;
            } else if (responseDto["status"] === "Successful") {
                clearInterval(this.intervalId);
                this.setState({ "show_authentication_view": false });
                this.pay();
            } else {
                clearInterval(this.intervalId);
                this.setState({ "show_authentication_view": false });
                alert("Payment failed!");
                window.location.replace(this.state.failure_url + "?error_message=DO_NOT_PROCEED");
            }
        }
        else if (this.ObjectContains(responseDto, "succeeded") && !responseDto["succeeded"]) {
            this.setState({ "show_authentication_view": false });
            alert(responseDto["error_message"]);
        }
        else {
            this.setState({ "show_authentication_view": false });
            alert("Something went wrong, try again later");
        }
    }

    onGetSessionInfosSuccess = (responseDto) => {
        this.setState({ "show_loading": false });
        if (this.ObjectContains(responseDto, "succeeded") && responseDto["succeeded"]) {
            if (responseDto["session_status"] === SessionStatus.Completed) {
                this.setState({ "show_payment_completed": true });
                return;
            }

            if (!responseDto["is_bank_card_enabled"] &&
                !responseDto["is_meeza_card_enabled"] &&
                !responseDto["is_wallet_enabled"]) {
                alert('No payment method is allowed for you');
                this.setState({ "show_not_found": true });
            } else {
                let paymentMethodType = PaymentMethodType.Unknown;
                if (responseDto.payment_method_type != null && responseDto.payment_method_type !== PaymentMethodType.Unknown) {
                    paymentMethodType = responseDto.payment_method_type;
                    this.setState({ is_payment_method_type_preselected: true });
                } else if (responseDto["is_bank_card_enabled"]) {
                    paymentMethodType = PaymentMethodType.BankCard;
                } else if (responseDto["is_meeza_card_enabled"]) {
                    paymentMethodType = PaymentMethodType.MeezaCard;
                } else if (responseDto["is_wallet_enabled"]) {
                    paymentMethodType = PaymentMethodType.Wallet;
                }

                this.setState({ "payment_method_type": paymentMethodType })
            }

            this.setState({
                "is_save_card_checkbox_enabled": responseDto["is_save_card_checkbox_enabled"],
                "is_bank_card_enabled": responseDto["is_bank_card_enabled"],
                "is_meeza_card_enabled": responseDto["is_meeza_card_enabled"],
                "is_wallet_enabled": responseDto["is_wallet_enabled"],
                "is_instapay_enabled": responseDto["is_instapay_enabled"],
                "is_3ds_required": responseDto["is_3ds_required"],
                "success_url": responseDto["success_url"],
                "failure_url": responseDto["failure_url"],
                "amount": responseDto["amount"],
                "should_show_amount": responseDto["should_show_amount"],
                "currency": responseDto["currency"],
                "is_apple_pay_enabled": (responseDto["is_apple_pay_enabled"] ?? false) && this.isApplePayAvailable(),
                "apple_pay_merchant_id": responseDto["apple_pay_merchant_id"],
                "merchant_display_name": responseDto['merchant_display_name'],
            });
        }
        else if (this.ObjectContains(responseDto, "succeeded") && !responseDto["succeeded"]) {
            alert(responseDto["error_message"]);
            window.location.replace(responseDto["failure_url"] + "?error_code=" + responseDto["error_code"] + "&error_message=" + responseDto["error_message"]);
        }
        else {
            alert("Something went wrong, try again later");
        }
    }

    toQueryParameters(response) {
        return Object.keys(response)
            .filter(key => key !== 'redirection_url')
            .map(key => {
                return encodeURIComponent(key) + "=" + encodeURIComponent(JSON.stringify(response[key]));
            }).join('&');
    }

    onPaySuccess = (responseDto) => {
        this.postMessage(responseDto);
        this.setState({ "show_loading": false });
        this.setState({ "show_authentication_view": false });
        if (this.ObjectContains(responseDto, "succeeded") && responseDto["succeeded"]) {
            if (!responseDto["error_message"] && responseDto["transaction_status"] === TransactionStatus.Successful) {
                this.setState({ "show_payment_success": true });
                window.location.replace(responseDto["redirection_url"] + "?" + this.toQueryParameters(responseDto))
            } else {
                if (!responseDto["error_message"]) {
                    responseDto["error_message"] = "Payment failed";
                }

                window.location.replace(responseDto["redirection_url"] + "?error_code=" + responseDto["error_code"] + "&error_message=" + responseDto["error_message"]);
            }
        }
        else if (this.ObjectContains(responseDto, "succeeded") && !responseDto["succeeded"]) {
            alert(responseDto["error_message"]);
        }
        else {
            alert("Something went wrong");
        }
    }

    onError = (error) => {
        this.resetState();
        console.log(error);
        alert(error.message);
    }

    resetState() {
        this.setState({
            form: {
                first_name: '',
                last_name: '',
                payment_number: '',
                expiry_date: '',
                cvv: '',
            },
            valid_payment_number: false,
            valid_expiration_month: false,
            valid_expiration_year: false,
            valid_first_name: false,
            valid_last_name: false,
            valid_cvv: false,
            show_payment_success: false,
            show_loading: false,
            show_not_found: false,
            authentication_view_html: '',
            authentication_view_url: '',
            show_authentication_view: false,
            pay_button_clicked: false,
            should_show_amount: false,
        });
    }

    showPaymentSuccess() {
        return (
            <div>
                <h3 className='text-center text-success mt-4'>Payment succeeded!</h3>
                <br />
                <PoweredByBox />
                <br />
            </div>
        )
    }

    showNotFound() {
        return (
            <div>
                <h3 className='text-center text-danger mt-4'>Payment not found!</h3>
                <br />
                <PoweredByBox />
                <br />
            </div>
        )
    }

    showPaymentCompleted() {
        return (
            <div>
                <h3 className='text-center text-danger mt-4'>Payment has been completed before!</h3>
                <br />
                <PoweredByBox />
                <br />
            </div>
        )
    }

    handleBorderDangerClass(valid) {
        return valid || !this.state.pay_button_clicked ? '' : 'border-danger'
    }

    handleShowAmount() {
        return this.state.should_show_amount === true && this.state.amount !== null ?
            `${this.state.amount.toFixed(2)} ${this.state.currency}` : '';
    }

    showPaymentCardForm () {
        return (
            <div>
                <Form className="ml-3 mt-2">
                    <Form.Group controlId="payment_number" className="pt-4" >
                        <Form.Control
                            className={this.handleBorderDangerClass(this.state.valid_payment_number)}
                            type="text"
                            onInput={(event) => { ValidateNumeric(event); FormatCardNumber(event); }}
                            maxLength={19}
                            placeholder="Card Number"
                            onChange={this.handleChange("payment_number")}
                        />
                    </Form.Group>
                    <div className="row">
                        <div className="col-6">
                            <Form.Group controlId="first_name" className="pt-4">
                                <Form.Control
                                    className={this.handleBorderDangerClass(this.state.valid_first_name)}
                                    type="text"
                                    maxLength={50}
                                    placeholder="First Name"
                                    onChange={this.handleChange("first_name")}
                                />
                            </Form.Group>
                        </div>
                        <div className="col-6">
                            <Form.Group controlId="last_name" className="pt-4">
                                <Form.Control
                                    className={this.handleBorderDangerClass(this.state.valid_last_name)}
                                    type="text"
                                    maxLength={50}
                                    placeholder="Last Name"
                                    onChange={this.handleChange("last_name")}
                                />
                            </Form.Group>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-6">
                            <Form.Group controlId="expiry_date" className="pt-4">
                                <Form.Control
                                    className={this.handleBorderDangerClass(this.state.valid_expiration_month && this.state.valid_expiration_year)}
                                    type="text"
                                    maxLength={5}
                                    onInput={(event) => { ValidateNumeric(event); FormatExpiryDate(event, this.state.form.expiry_date); }}
                                    placeholder="MM/YY"
                                    onChange={this.handleChange("expiry_date")}
                                />
                            </Form.Group>
                        </div>
                        <div className="col-6">
                            <Form.Group controlId="cvv" className="pt-4">
                                <Form.Control
                                    className={this.handleBorderDangerClass(this.state.valid_cvv)}
                                    type="text"
                                    placeholder="CVV"
                                    maxLength={3}
                                    onInput={(event) => ValidateNumeric(event)}
                                    onChange={this.handleChange("cvv")}
                                />
                            </Form.Group>
                        </div>
                    </div>
                    {
                        !this.state.is_save_card_checkbox_enabled ? null :
                            (
                                <Form.Group controlId="should_save_payment_method" className="pt-4">
                                    <Form.Check style={{ marginLeft: '40%' }} type="checkbox" id="custom-switch" label="Save card" onChange={() => this.setState({ should_save_payment_method: !this.state.should_save_payment_method })} />
                                </Form.Group>
                            )
                    }
                </Form>
                <br />
                <div className='text-center'>
                    <button className="btn btn-md pl-4 pr-4 primary-btn" style={{ width: "50%" }} onClick={(e) => this.onSubmit(e)}>
                        Pay {this.handleShowAmount()}
                    </button>
                </div>
                <br />
                <PoweredByBox />
                <br />
            </div>
        );
    }

    showWalletForm() {
        return (
            <div>
                <Form className="ml-3 mt-2">
                    <Form.Group controlId="payment_number" className="pt-4" >
                        <Form.Control
                            className={this.handleBorderDangerClass(this.state.valid_payment_number)}
                            type="text"
                            value={this.state.form.payment_number}
                            onInput={(event) => { ValidateNumeric(event); }}
                            maxLength={19}
                            placeholder="Wallet Number"
                            onChange={this.handleChange("payment_number")}
                        />
                    </Form.Group>
                </Form>
                <br />
                <div className="text-center">
                    <button className="btn btn-md primary-btn pl-4 pr-4" style={{ width: "50%" }} onClick={(e) => this.onSubmit(e)}>
                        Pay {this.handleShowAmount()}
                    </button>
                </div>
                <br />
                <PoweredByBox />
                <br />
            </div>)
    }

    showAuthenticationView() {
        return (
            <AuthenticationView
                paymentMethodType={this.state.payment_method_type}
                authViewHtml={this.state.authentication_view_html}
                authViewUrl={this.state.authentication_view_url} />
        );
    }

    showLoading() {
        return (
            <LoadingView color={this.primaryColor} />
        );
    }

    getCardBackgroudColor(paymentMethod) {
        return this.state.payment_method_type === paymentMethod ? this.primaryColor : "#f0f0f0"
    }

    getCardTextColor(paymentMethod) {
        return this.state.payment_method_type === paymentMethod ? "#fff" : "#454545"
    }

    changeSelectedPaymentMethod = (value) => {
        const nextPaymentMethodType = Number(value);
        this.setState({
            payment_method_type: Number.isFinite(nextPaymentMethodType) ? nextPaymentMethodType : PaymentMethodType.Unknown,
            pay_button_clicked: false,
            valid_payment_number: false,
            valid_expiration_month: false,
            valid_expiration_year: false,
            valid_first_name: false,
            valid_last_name: false,
            valid_cvv: false,
        });
    }

    showTab(paymentMethodType, label, borderStyle = 'none') {
        const count = this.state.is_payment_method_type_preselected ? 1 : 
            (this.state.is_bank_card_enabled | this.state.is_meeza_card_enabled)
            + this.state.is_wallet_enabled + this.state.is_instapay_enabled + this.state.is_apple_pay_enabled;
        const col = parseInt(12 / count);
        let className = `col-md-${col} col-xs-12`;

        return (
            <div
                className={className + " btn btn-md"}
                style={{ backgroundColor: this.getCardBackgroudColor(paymentMethodType), color: this.getCardTextColor(paymentMethodType), borderRadius: 0, borderLeft: `1px ${borderStyle} #EAEAEA` }}
                onClick={() => this.changeSelectedPaymentMethod(paymentMethodType)}
            >
                {label}
            </div>
        );
    }

    getTabs(cardTitle) {
        if (this.state.is_payment_method_type_preselected) {
            return this.state.payment_method_type === PaymentMethodType.Wallet ?
                this.showTab(PaymentMethodType.Wallet, "Digital Wallet", 'none') :
                 this.state.payment_method_type === PaymentMethodType.BankCard ?
                    this.showTab(PaymentMethodType.BankCard, "Bank Card") :
                        this.showTab(PaymentMethodType.MeezaCard, "Meeza Card");
        }
        else {
            return (
                <>
                    {
                        this.state.is_bank_card_enabled || this.state.is_meeza_card_enabled ?
                            this.showTab(PaymentMethodType.BankCard, cardTitle) : null
                    }
                    {
                        this.state.is_wallet_enabled ? this.showTab(PaymentMethodType.Wallet, "Digital Wallet", this.state.is_meeza_card_enabled ? 'solid' : 'none') : null
                    }
                    {
                        this.state.is_instapay_enabled ? this.showTab(PaymentMethodType.Instapay, "Instapay") : null
                    }
                    {
                        this.state.is_apple_pay_enabled ? this.showTab(PaymentMethodType.ApplePay, "Apple Pay") : null
                    }
                </>
            );
        }
    }

    showForm() {
        if (this.state.payment_method_type === PaymentMethodType.Unknown) {
            return null;
        }

        let cardTitle = null;

        if (this.state.is_bank_card_enabled && this.state.is_meeza_card_enabled) {
            cardTitle = "Bank/Meeza Card";
        } else if (this.state.is_bank_card_enabled) {
            cardTitle = "Bank Card";
        } else if (this.state.is_meeza_card_enabled) {
            cardTitle = "Meeza Card";
        }

        return (
            <div>
                {
                    this.state.show_payment_tabs &&
                    <div className="row">
                        {this.getTabs(cardTitle)}
                    </div>
                }
                <div className="row mt-3">
                    {this.state.payment_method_type === PaymentMethodType.Wallet && this.showWalletForm()}
                    {this.state.payment_method_type === PaymentMethodType.Instapay && 
                        <InstapayPaymentForm
                            onChangeTapsVisiability={(show_payment_tabs) => this.setState({ show_payment_tabs  })}
                            payment_session_info={{
                                    sessionId: this.sessionId, 
                                    authToken: this.authToken,
                                    amount_to_pay: this.state.amount,
                                    currency: this.state.currency,
                                    should_show_amount: this.state.should_show_amount,
                                    success_url: this.state.success_url,
                                    failure_url: this.state.failure_url,
                                }}
                            />}
                    {this.state.payment_method_type === PaymentMethodType.ApplePay && 
                        <ApplePayForm
                            onPaymentChargeSuccessful={this.onPaySuccess}
                            onPaymentChargeFailed={this.onError}
                            payment_session_info={{
                                    sessionId: this.sessionId, 
                                    authToken: this.authToken,
                                    amount_to_pay: this.state.amount,
                                    currency: this.state.currency,
                                    should_show_amount: this.state.should_show_amount,
                                    merchant_name: this.state.merchant_display_name,
                                    merchant_id: this.state.apple_pay_merchant_id,
                                    failure_url: this.state.failure_url,
                                    success_url: this.state.success_url,
                                }}
                            />}
                    {(this.state.payment_method_type === PaymentMethodType.BankCard || this.state.payment_method_type === PaymentMethodType.MeezaCard) && this.showPaymentCardForm()}
                </div>
            </div>
        )
    }

    getMainClassName() {
        if (this.state.show_loading !== true) {
            return "col-lg-7 p-4 main-box";
        }

        return "col-lg-7 p-4";
    }

    render() {
        if (this.useCardDetailsV2 === "True") {
            return <HostedPaymentCheckoutV2 />
        }
        return (
            <Container style={{ marginTop: "50px" }}>
                <div className="row p-4">
                    <div className='col-lg-2'></div>
                    <div className={this.getMainClassName()}>
                        {
                            this.state.show_not_found ? this.showNotFound() :
                                this.state.show_payment_completed ? this.showPaymentCompleted() :
                                    this.state.show_loading ? this.showLoading() :
                                        this.state.show_authentication_view ? this.showAuthenticationView() :
                                            this.state.show_payment_success ? this.showPaymentSuccess() : this.showForm()
                        }
                    </div>
                </div>
            </Container>
        );
    }
}

export default HostedPaymentCheckout;
